/**
 * Dispatches unit commands to a site's device and reports the batch outcome.
 *
 * Wraps the site-command endpoint so command dialogs share one submit path:
 * tracks the in-flight `submitting` flag, classifies the resolved
 * { pending_commands, errors } envelope (200 success / 207 partial / in-body
 * 400 failed) via classifyDispatchEnvelope, and routes a thrown failure
 * (network error, HTTP 400) through the canonical parseApiError so its
 * human-readable messages surface the same way every form's do. A thrown
 * failure also carries its raw response body back, so a consumer can render a
 * structured refusal (an incompatible-firmware-model list, say) that a flat
 * message string would lose. Consumers decide how to present the outcome
 * (toast, banner, close-on-success).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { dispatchUnitCommands } from '@sophocles/api/odlApp';
import {
  beginCommandDispatch,
  endCommandDispatch,
  markUpgradeStarting,
  promoteCommandDispatch,
  selectSiteCommandLocked,
} from '@sophocles/features/entities/site';
import { classifyDispatchEnvelope, DISPATCH_STATUS } from '@sophocles/hooks/commandDispatchResult';
import { parseApiError } from '@sophocles/utils/apiErrors';

const LOCKED_RESULT = Object.freeze({
  status: DISPATCH_STATUS.FAILED,
  pendingCommands: [],
  failures: [{ message_key: 'commandActivity.commandsLocked' }],
  messages: [],
  errorBody: null,
});

/**
 * @param {number|string} siteId  site whose device receives the commands
 * @returns {{ dispatchCommands: (commands: Array<{key: string, params?: object}>) =>
 *   Promise<{ status: string, pendingCommands: object[], failures: object[], messages: string[],
 *     errorBody: object|null }>,
 *   submitting: boolean, activityLocked: boolean }}
 */
export default function useCommandDispatch(siteId) {
  const dispatch = useDispatch();
  const activityLocked = useSelector(selectSiteCommandLocked(siteId));
  const [submitting, setSubmitting] = useState(false);
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const activityLockedRef = useRef(activityLocked);

  useEffect(() => {
    activityLockedRef.current = activityLocked;
  }, [activityLocked]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const dispatchCommands = useCallback(
    async commands => {
      if (activityLockedRef.current || inFlightRef.current) return LOCKED_RESULT;

      inFlightRef.current = true;
      const requestId = dispatch(beginCommandDispatch({ siteId }));
      if (requestId == null) {
        inFlightRef.current = false;
        return LOCKED_RESULT;
      }
      setSubmitting(true);
      try {
        const envelope = await dispatchUnitCommands(siteId, commands);
        const result = { ...classifyDispatchEnvelope(envelope), messages: [], errorBody: null };
        const queueBackedCommands = result.pendingCommands.filter(command => command?.id != null);
        if (queueBackedCommands.length > 0) {
          dispatch(
            promoteCommandDispatch({
              siteId,
              requestId,
              pendingCount: queueBackedCommands.length,
            }),
          );
        }
        const firmwareBatch = result.pendingCommands.find(command => command?.firmware_batch_id != null);
        if (firmwareBatch) {
          dispatch(markUpgradeStarting({ siteId, batchId: firmwareBatch.firmware_batch_id }));
        }
        return result;
      } catch (error) {
        return {
          status: DISPATCH_STATUS.FAILED,
          pendingCommands: [],
          failures: [],
          messages: parseApiError(error).nonFieldMessages,
          errorBody: error?.response?.data ?? null,
        };
      } finally {
        dispatch(endCommandDispatch({ siteId, requestId }));
        inFlightRef.current = false;
        if (mountedRef.current) {
          setSubmitting(false);
        }
      }
    },
    [dispatch, siteId],
  );

  return useMemo(
    () => ({ dispatchCommands, submitting, activityLocked }),
    [activityLocked, dispatchCommands, submitting],
  );
}
